import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import { getProfileMeta, isTeamProfile } from '@/services/userPermissions';
import ApperIcon from '@/components/ApperIcon';
import ThemeToggle from '@/components/ThemeToggle';
import AccountDialog from '@/components/AccountDialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useT } from '@/shared/i18n';

export default function UserMenu({ user: userProp, initials: initialsProp, displayName: displayNameProp, logout: logoutProp }) {
  const { t } = useT();
  const auth = useAuth();
  const user = userProp ?? auth.user;
  const logout = logoutProp ?? auth.logout;
  const displayName =
    displayNameProp ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.emailAddress ||
    'Account';
  const initials =
    initialsProp ||
    displayName
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() ||
    'GC';
  const meta = getProfileMeta(user?.profile);
  const profileLabel = meta?.label || String(user?.profile ?? '').replace(/_c$/, '');
  const team = isTeamProfile(user?.profile);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsTarget = useMemo(
    () =>
      detailsOpen
        ? { id: user?.userId, email: user?.emailAddress, firstName: user?.firstName, lastName: user?.lastName, name: user?.name }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detailsOpen],
  );

  return (
    <>
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full p-0.5 outline-none ring-ring transition-colors hover:bg-accent focus-visible:ring-2"
          aria-label="Account menu"
        >
          <Avatar className="size-9 ring-1 ring-highlight/50">
            <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden pr-2 text-left sm:block">
            <span className="block max-w-[9rem] truncate text-sm leading-tight">{displayName}</span>
            <span className="block text-[11px] leading-tight text-muted-foreground">{t(profileLabel)}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <p className="text-sm font-medium text-foreground">{displayName}</p>
          {user?.emailAddress && <p className="truncate text-xs text-muted-foreground">{user.emailAddress}</p>}
          {profileLabel && (
            <p className="mt-1 inline-block rounded-sm bg-highlight/15 px-1.5 py-0.5 text-[11px] text-highlight">{t(profileLabel)}</p>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">{t('Theme')}</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <ThemeToggle className="w-full justify-between" />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={team ? '/admin' : '/account'}>
            <ApperIcon name="LayoutDashboard" size={16} />
            {team ? t('Console') : t('My account')}
          </Link>
        </DropdownMenuItem>
        {meta?.canManageTeam && (
          <DropdownMenuItem asChild>
            <Link to="/admin/team">
              <ApperIcon name="UsersRound" size={16} />
              {t('Team')}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/">
            <ApperIcon name="Store" size={16} />
            {t('Storefront')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setDetailsOpen(true)}>
          <ApperIcon name="UserPen" size={16} />
          {t('My details')}
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/change-password">
            <ApperIcon name="KeyRound" size={16} />
            {t('Change password')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/install">
            <ApperIcon name="Smartphone" size={16} />
            {t('Install the app')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}>
          <ApperIcon name="LogOut" size={16} />
          {t('Sign out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <AccountDialog target={detailsTarget} self onOpenChange={setDetailsOpen} />
    </>
  );
}
